import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

/* import { CurrencyCode } from "@/lib/currency"; */

import type { MutationConfig } from "@/lib/react-query";
import type { PlanName } from "@/types/plans";

export const createPaymentInputSchema = z.object({
  tier_name: z.custom<PlanName>(),
  /*  currency: z.custom<CurrencyCode>, */ // Temporarily disabled until we support multiple currencies
});

export type CreatePaymentInput = z.infer<typeof createPaymentInputSchema>;

type CreatePaymentResponse = {
  url?: string;
};

export const createPayment = async ({
  data,
}: {
  data: CreatePaymentInput;
}): Promise<CreatePaymentResponse> => {
  if (!hasAccessToken()) {
    throw new Error("Missing access token");
  }

  return await api.post("/carburate/billing/checkout", data);
};

type UseCreatePaymentOptions = {
  mutationConfig?: MutationConfig<typeof createPayment>;
};

export const useCreatePayment = ({ mutationConfig }: UseCreatePaymentOptions = {}) => {
  const { onSuccess, onError, onMutate, ...restConfig } = mutationConfig || {};

  if (!hasAccessToken()) {
    throw new Error("Missing access token");
  }

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createPayment,
    onMutate: onMutate
      ? async (vars) => {
          await onMutate(vars, undefined as any);
        }
      : undefined,
    onError: (err, vars, context: any) => {
      onError?.(err, vars, context, undefined as any);
    },
    onSuccess: (data, ...args) => {
      // Invalidate payments list so billing history is refetched after creating a payment.
      // Do NOT invalidate ["plans"] here: the plan change is only confirmed after Stripe
      // processes the payment via webhook; the server will update the plan at that point.
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      onSuccess?.(data, ...args);
    },
    ...restConfig,
  });

  return { ...mutation };
};
